import React, { useRef } from 'react';
import { Card, useAccordionButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl/dist';
import { Element } from 'react-scroll';
import { faMicrophone } from '@fortawesome/free-solid-svg-icons';

import useDisclosure from '../../../hooks/useDisclosure';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { CustomerNotesSection } from './CustomerNotesSection';
import { EventComments } from './EventComments';
import AddCallNoteModal from './AddCallNoteModal';
import {
    StyledAccordionHeaderContainer,
    StyledAccordionHeaderRole,
    StyledAccordionHeaderTimestamp,
    StyledAccordionHeaderTitle,
    StyledEventContainer,
    StyledEventKey,
    StyledEventValue,
    StyledHr,
    TextEllipsis200,
    StyledUserRole,
    StyledAccordionSubHeader,
    StyledCard,
    StyledCardHeader,
    StyledAccordionCollapse,
    StyledAccordion,
    StyledScrollElement,
    StyledAccordionHeader,
} from './AccountActivity.styled';
import { useAccountActivityState } from '../../../hooks/useAccountActivityState';
import { useAppSelector } from '../../../hooks';
import {
    CATEGORY_TYPES,
    COLON_SEPARATOR,
    CUSTOMER_CALLS_LOGGED_CALL_EVENT,
    CUSTOMER_INTERACTIONS_CHAT_EVENT,
    CUSTOMER_TICKET_COMMENTS_EVENT,
    CUSTOMER_TICKET_TRANSFERRED_EVENT,
    IS_TICKET_COMMENT_DELETED,
    IS_TICKET_COMMENT_PUBLIC,
    MAP_PARAMETERS,
    NOTE_TYPES,
    TICKET_COMMENT_HEADER_LENGTH,
} from './common/accountActivity.constants';
import { M_D_YYYY_H_MM_A, formatDateString } from '../../../utils/dates/dateMethods';
import endpoints from '../../../constants/configs/endpoints';

interface IAccountActivityEventsProps {
    activeKey: string | null;
    setActiveKey: React.Dispatch<React.SetStateAction<string | null>>;
    fetchComments: (eventId: string) => void;
}

const EXPANDED_ICON = 'chevron-up';
const COLLAPSED_ICON = 'chevron-down';

function CustomToggle({
    item,
    event,
    activeKey,
    setActiveKey,
    viewTicketDetailsLink,
    fetchComments,
    onAddCallNoteModalOpen,
    onAddChatNoteModalOpen,
}) {
    const isActive = activeKey === item.id;
    const { isLinkForEventName } = useAccountActivityState();
    const onAccordionBtnClick = useAccordionButton(item.id, () => {
        setActiveKey(isActive ? null : item.id);
    });

    const getEventName = () => {
        const eventName = `${item.type} - ${item.name}`;
        return isLinkForEventName ? <a>{eventName}</a> : <span>{eventName}</span>;
    };

    const isCommentDeleted =
        event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT_DELETED]?.toLowerCase() === IS_TICKET_COMMENT_DELETED;

    const itemType = item.type.toLowerCase();
    const itemName = item.name.toLowerCase();

    const TicketDetailsLink = ({ event }: { event: any }) => (
        <span data-testid="ticket-details-link">
            <ConditionalRendering isTrue={!viewTicketDetailsLink}>
                {event.mapParameters?.[MAP_PARAMETERS.TICKET_NUMBER]}
            </ConditionalRendering>
            <ConditionalRendering isTrue={viewTicketDetailsLink}>
                <a
                    href={endpoints.TICKET_MGMT_TICKET_DETAIL.processReplacements({
                        encTicketId: event.Enc_Ticket_Number,
                    })}
                    target="_blank"
                >
                    {event.mapParameters?.[MAP_PARAMETERS.TICKET_NUMBER]}
                </a>
            </ConditionalRendering>
        </span>
    );

    const TicketTransferredEventSubHeader = ({ event }: { event: any }) => (
        <ConditionalRendering isTrue={itemName === CUSTOMER_TICKET_TRANSFERRED_EVENT.toLowerCase()}>
            <span>{COLON_SEPARATOR}</span>
            <ConditionalRendering
                isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_TRANSFER_MSG]}
            >
                <span className="ticket-subHeader" data-testid="ticket-transferred-event-subHeader">{event.mapParameters?.[MAP_PARAMETERS.TICKET_TRANSFER_MSG]}</span>
            </ConditionalRendering>
        </ConditionalRendering>
    );

    const TicketCommentsEventSubHeder = ({ event }: { event: any }) => (
        <ConditionalRendering isTrue={itemName === CUSTOMER_TICKET_COMMENTS_EVENT.toLowerCase()}>
            <span>{COLON_SEPARATOR}</span>
            <ConditionalRendering isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}>
                <span className="ticket-subHeader" data-testid="ticket-subject">{event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}</span>
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCommentDeleted}>
                <i className="ticket-subHeader" data-testid="comment-deleted"><FormattedMessage id="member.account.activity.ticket.comment.deleted" /></i>
            </ConditionalRendering>
            <ConditionalRendering isTrue={!isCommentDeleted}>
                <ConditionalRendering
                    isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT]}
                >
                    <span> - </span>
                    <span className="ticket-subHeader" data-testid="ticket-comment-id">{event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT]?.substring(0, TICKET_COMMENT_HEADER_LENGTH) || ''}</span>
                </ConditionalRendering>
                <ConditionalRendering
                    isTrue={event.mapParameters?.[MAP_PARAMETERS.TICKET_PUBLIC_COMMENT] === IS_TICKET_COMMENT_PUBLIC}
                >
                    <i className="ticket-subHeader" data-testid="public-comment"><FormattedMessage id="member.account.activity.ticket.comment.public" /></i>
                </ConditionalRendering>
            </ConditionalRendering>
        </ConditionalRendering>
    );

    const TicketEventsSubHeader = ({ event }: { event: any }) => (
        <ConditionalRendering
            isTrue={
                itemName !== CUSTOMER_TICKET_COMMENTS_EVENT.toLowerCase() &&
                itemName !== CUSTOMER_TICKET_TRANSFERRED_EVENT.toLowerCase()
            }
        >
            <ConditionalRendering isTrue={!event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}>
                <span className="ticket-subHeader">
                    <FormattedMessage id="member.account.activity.event.of.workgroup" />
                </span>
                <span className="ticket-subHeader" data-testid="ticket-workgroup">{event.mapParameters?.[MAP_PARAMETERS.TICKET_WORKGROUP]}</span>
            </ConditionalRendering>
            <ConditionalRendering isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}>
                <span className="ticket-subHeader" data-testid="ticket-subject">{`: ${event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}`}</span>
            </ConditionalRendering>
        </ConditionalRendering>
    );

    return (
        <StyledAccordionHeaderContainer onClick={onAccordionBtnClick}>
            <StyledAccordionHeader>
                <StyledAccordionHeaderTitle data-testid="event-name">{getEventName()}</StyledAccordionHeaderTitle>
                <StyledAccordionHeaderTimestamp data-testid="event-timestamp">{`${formatDateString(
                    item.creationTimeStamp,
                    M_D_YYYY_H_MM_A,
                )}`}</StyledAccordionHeaderTimestamp>
                <StyledAccordionHeaderRole data-testid="event-role">
                    {`${item.createdByUserName} - (`}
                    <StyledUserRole>{`${item.createdByUserRole?.toLowerCase()})`}</StyledUserRole>
                </StyledAccordionHeaderRole>
                <FontAwesomeIcon icon={isActive ? EXPANDED_ICON : COLLAPSED_ICON} />
            </StyledAccordionHeader>
            <ConditionalRendering isTrue={itemType === CATEGORY_TYPES.TICKETS.toLowerCase()}>
                <StyledHr />
                <StyledAccordionSubHeader data-testid="event-subHeader">
                    <TicketDetailsLink event={event} />
                    <TicketTransferredEventSubHeader event={event} />
                    <TicketCommentsEventSubHeder event={event} />
                    <TicketEventsSubHeader event={event} />
                </StyledAccordionSubHeader>
            </ConditionalRendering>
            <ConditionalRendering
                isTrue={
                    itemType === CATEGORY_TYPES.CUSTOMER_CALLS.toLowerCase() &&
                    itemName === CUSTOMER_CALLS_LOGGED_CALL_EVENT.toLowerCase()
                }
            >
                <StyledHr />
                <CustomerNotesSection
                    item={item}
                    fetchComments={fetchComments}
                    activeKey={activeKey}
                    title="member.account.activity.note.call.title"
                    buttonLabel="member.account.activity.note.call.button"
                    buttonId="add_call_note_btn"
                    onButtonClick={onAddCallNoteModalOpen}
                />
            </ConditionalRendering>
            <ConditionalRendering
                isTrue={
                    itemType === CATEGORY_TYPES.CUSTOMER_INTERACTIONS.toLowerCase() &&
                    itemName === CUSTOMER_INTERACTIONS_CHAT_EVENT.toLowerCase()
                }
            >
                <StyledHr />
                <CustomerNotesSection
                    item={item}
                    fetchComments={fetchComments}
                    activeKey={activeKey}
                    title="member.account.activity.note.chat.title"
                    buttonLabel="member.account.activity.note.chat.button"
                    buttonId="add_chat_note_btn"
                    onButtonClick={onAddChatNoteModalOpen}
                />
            </ConditionalRendering>
        </StyledAccordionHeaderContainer>
    );
}

export const AccountActivityEvents: React.FC<IAccountActivityEventsProps> = ({
    activeKey,
    setActiveKey,
    fetchComments,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef(null);
    const intlApi = useIntl();
    const { initialData, currentFilteredData } = useAppSelector(({ accountActivity }) => accountActivity);

    const {
        viewTicketDetailsLink,
        memberHistoryPermissionMap: { memberAbtShowcomments },
    } = initialData;

    const {
        isOpen: isAddCallNoteModalOpen,
        onOpen: onAddCallNoteModalOpen,
        onClose: onAddCallNoteModalClose,
    } = useDisclosure(false);

    const {
        isOpen: isAddChatNoteModalOpen,
        onOpen: onAddChatNoteModalOpen,
        onClose: onAddChatNoteModalClose,
    } = useDisclosure(false);

    const interactionIdkeyLabelMap = [
        { key: 'SessionId', label: 'Session Id' },
        { key: 'PrimaryReason', label: 'Primary Reason', isEllipsis: true },
        { key: 'StyledComment', label: 'Comments' },
        { key: 'Disposition', label: 'Disposition' },
        { key: 'Started', label: 'Started' },
        { key: 'Ended', label: 'Ended' },
        { key: 'Duration', label: 'Duration' },
        { key: 'Agent Name', label: 'CSR' },
        { key: 'Skill', label: 'Skill' },
    ];

    function formatFieldName(key: string): string {
        if (!key) return '';

        // Capitalize the first letter and split the string into words at camelCase transitions
        return key
            .replace(/^./, match => match.toUpperCase()) // Capitalize the first character
            .replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space before capital letters not preceded by another capital
    }

    const onAddNoteModalClose = (type: NOTE_TYPES) => {
        type === NOTE_TYPES.CALL ? onAddCallNoteModalClose() : onAddChatNoteModalClose();
    };

    const getEventMapParameters = (event): JSX.Element | React.ReactNode => {
        return (
            event.mapParameters &&
            Object.entries(event.mapParameters).map(([_, mapParam]: [any, any]) => (
                <React.Fragment key={mapParam.SessionId || Math.random()}>
                    {interactionIdkeyLabelMap.map(({ key, label, isEllipsis }) => (
                        <StyledEventContainer columns="2" key={key}>
                            <StyledEventKey data-testid={`${label}-key`}>{label}</StyledEventKey>
                            <StyledEventValue data-testid={`${label}-value`}>
                                <ConditionalRendering isTrue={isEllipsis}>
                                    <TextEllipsis200 title={mapParam[key]}>{mapParam[key]}</TextEllipsis200>
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={!isEllipsis}>
                                    mapParam[key]
                                </ConditionalRendering>
                            </StyledEventValue>
                        </StyledEventContainer>
                    ))}
                </React.Fragment>
            ))
        );
    };

    const TicketComment = ({ item, event }) => {
        const isCommentDeleted =
            event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT_DELETED]?.toLowerCase() === IS_TICKET_COMMENT_DELETED;

        return (
            <ConditionalRendering isTrue={item.name === CUSTOMER_TICKET_COMMENTS_EVENT}>
                <ConditionalRendering
                    isTrue={event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT] && !isCommentDeleted}
                >
                    {event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT]}
                </ConditionalRendering>
            </ConditionalRendering>
        );
    };

    return (
        <>
            <StyledAccordion className="styled-accordion" ref={containerRef} flush activeKey={activeKey}>
                <StyledScrollElement name="containerElement" id="containerElement">
                    {currentFilteredData.map(item => {
                        const event = JSON.parse(item.eventJson);

                        return (
                            <StyledCard
                                id={`card-${item.id}`}
                                data-testid={`card-${item.id}`}
                                key={item.id}
                                isActive={activeKey === item.id}
                            >
                                <Element name={item.id} id={item.id}>
                                    <StyledCardHeader id={item.id} ref={headerRef} isActive={activeKey === item.id}>
                                        <CustomToggle
                                            event={event}
                                            item={item}
                                            activeKey={activeKey}
                                            setActiveKey={setActiveKey}
                                            viewTicketDetailsLink={viewTicketDetailsLink}
                                            fetchComments={fetchComments}
                                            onAddCallNoteModalOpen={onAddCallNoteModalOpen}
                                            onAddChatNoteModalOpen={onAddChatNoteModalOpen}
                                        />
                                    </StyledCardHeader>
                                    <StyledAccordionCollapse eventKey={item.id} isActive={activeKey === item.id}>
                                        <Card.Body>
                                            <div data-testid={`${item.id}-event-body`}>
                                                <TicketComment event={event} item={item} />
                                                <ConditionalRendering isTrue={item.name !== CUSTOMER_TICKET_COMMENTS_EVENT}>
                                                    <ConditionalRendering isTrue={!!event.recordingUrl}>
                                                        <a
                                                            href={event.recordingUrl}
                                                            target="_blank"
                                                            title={intlApi.formatMessage({ id: 'member.account.activity.recordingUrl.title' })}
                                                        >
                                                            <FontAwesomeIcon icon={faMicrophone} />
                                                        </a>
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={!!event.nextSteps}>
                                                        <StyledEventContainer columns="2">
                                                            <StyledEventKey data-testid="next-steps-key"><FormattedMessage id="member.account.activity.next.steps" /></StyledEventKey>
                                                            <StyledEventValue data-testid="next-steps-value">{event.nextSteps}</StyledEventValue>
                                                        </StyledEventContainer>
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={!event.interationId}>
                                                        {event.mapParameters &&
                                                            Object.entries(event.mapParameters)
                                                                .filter(([key]) => key !== MAP_PARAMETERS.TICKET_TRANSFER_MSG)
                                                                .map(([key, value]) => (
                                                                    <StyledEventContainer columns="2" key={key}>
                                                                        <StyledEventKey data-testid={`${key}-key`}> {formatFieldName(key)} </StyledEventKey>
                                                                        <StyledEventValue data-testid={`${key}-value`}>{value}</StyledEventValue>
                                                                    </StyledEventContainer>
                                                                ))}
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={!!event.interationId}>
                                                        <StyledEventContainer columns="2">
                                                            <StyledEventKey data-testid="interaction-id-key"><FormattedMessage id="member.account.activity.interaction.id" /></StyledEventKey>
                                                            <StyledEventValue data-testid="interaction-id-value">{event.interationId}</StyledEventValue>
                                                        </StyledEventContainer>
                                                        {getEventMapParameters(event)}
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={memberAbtShowcomments}>
                                                        <EventComments item={item} fetchComments={id => fetchComments(id)} />
                                                    </ConditionalRendering>
                                                </ConditionalRendering>
                                            </div>
                                        </Card.Body>
                                    </StyledAccordionCollapse>
                                </Element>
                            </StyledCard>
                        );
                    })}
                </StyledScrollElement>
            </StyledAccordion>
            <AddCallNoteModal
                id={activeKey}
                type={NOTE_TYPES.CALL}
                isOpen={isAddCallNoteModalOpen}
                onClose={() => onAddNoteModalClose(NOTE_TYPES.CALL)}
                refetchComments={() => fetchComments(activeKey!)}
            />
            <AddCallNoteModal
                id={activeKey}
                type={NOTE_TYPES.CHAT}
                isOpen={isAddChatNoteModalOpen}
                onClose={() => onAddNoteModalClose(NOTE_TYPES.CHAT)}
                refetchComments={() => fetchComments(activeKey!)}
            />
        </>
    );
};
